import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { Path } from 'constants/router'
import { ProductKind } from 'constants/product'
import { externalNavigate } from 'utils/helpers'
import { generatePath } from 'react-router-dom'
import { logger } from 'utils/logging'
import { useCallback, useMemo } from 'react'
import { useUserData } from 'components/contexts/UserDataContext'

interface OpenGuidelinesArgs {
  productKind: ProductKind | null | undefined
  creativeInstructionsPairingId: string | null | undefined
  legacyExternalUrl: string | null | undefined
}

/**
 * Builds the in-platform guidelines URL (`/guidelines/{productKind}` or the
 * `/guidelines` preview fallback), carrying the per-assignment
 * `creativeInstructionsPairingId` and the resolved `language` as query params.
 * Exported so the CT upload submit modal renders the same link target.
 */
export const buildInternalUrl = (
  productKind: ProductKind | null | undefined,
  creativeInstructionsPairingId: string | null | undefined,
  language: string
): string => {
  const base = productKind
    ? generatePath(Path.CREATIVE_GUIDELINES_FOR_KIND, { productKind })
    : Path.CREATIVE_GUIDELINES_PREVIEW

  const params: string[] = []
  if (creativeInstructionsPairingId) params.push(`creativeInstructionsPairingId=${encodeURIComponent(creativeInstructionsPairingId)}`)
  if (language) params.push(`language=${encodeURIComponent(language)}`)
  return params.length ? `${base}?${params.join('&')}` : base
}

/**
 * Helpers for opening the CT guidelines.
 *
 * Behind ALLOW_CT_GUIDELINES_PAGE: opens the in-platform
 * `/guidelines/{productKind}` page in a new tab, carrying the
 * `creativeInstructionsPairingId` (when from an assignment button) so the
 * page can render the orange instructions banner, plus a `language` param
 * derived from the current CT/Admin user (falls back to `en`).
 * Otherwise: falls back to the legacy external Retool URL.
 */
export const useOpenCreativeGuidelines = () => {
  const allowInternalPage = useFeatureFlag(FeatureFlag.ALLOW_CT_GUIDELINES_PAGE)
  const { baseUserData } = useUserData()
  const language = baseUserData?.language ?? 'en'

  const openOne = useCallback(({ productKind, creativeInstructionsPairingId, legacyExternalUrl }: OpenGuidelinesArgs) => {
    if (allowInternalPage) {
      logAnalyticsEvent(AnalyticsEvent.GUIDELINES_OPENED, { target: 'internal', productKind: productKind ?? null })
      window.open(buildInternalUrl(productKind, creativeInstructionsPairingId, language), '_blank', 'noopener,noreferrer')
      return
    }
    if (legacyExternalUrl) {
      logAnalyticsEvent(AnalyticsEvent.GUIDELINES_OPENED, { target: 'retool', productKind: productKind ?? null })
      externalNavigate(legacyExternalUrl)
      return
    }
    // Flag off and no legacy URL: the click resolves to no destination. Log it so a missing
    // creativeInstructionsUrl on an assignment surfaces in Sentry instead of being a silent no-op.
    logger.warn('Open guidelines requested but no destination resolved (flag off and no legacy URL)', { productKind: productKind ?? null }, { context: { component: 'useOpenCreativeGuidelines' } })
  }, [allowInternalPage, language])

  /**
   * Opens guidelines for many assignments at once (each in its own new tab).
   * Used by the grouped accept-terms link where one click should reveal every
   * assignment's guidelines. The window.open calls run synchronously inside the
   * originating click handler so browsers treat them as part of the same user
   * gesture and allow the multiple tabs (rather than blocking all but the first).
   */
  const openMany = useCallback((items: OpenGuidelinesArgs[]) => {
    items.forEach(openOne)
  }, [openOne])

  return useMemo(() => ({ open: openOne, openMany }), [openOne, openMany])
}
